import { PlusIcon, TrashIcon } from "@heroicons/react/outline";
import { observer } from "mobx-react-lite";

import { useSchemaEditor } from "..";

import { Button } from "@/components/ui/button";
import Checkbox from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Toggle } from "@/components/Toggle";
import HelpTooltip from "@/components/ui/HelpTooltip";
import { BasicTooltip } from "@/components/ui/Tooltip";

const Enums = ({
  includeEnum,
  enums,
  addEnum,
  setEnum,
  toggleEnums,
  deleteEnum,
  isEnumVariable,
  toggleEnumMode,
  onNameChange,
  enumName,
}: {
  includeEnum: boolean;
  enums: Record<string, string> | undefined;
  addEnum: () => void;
  setEnum: (key: string, value: string) => void;
  deleteEnum: (key: string) => void;
  toggleEnums: () => void;
  isEnumVariable: boolean;
  toggleEnumMode: () => void;
  onNameChange: (name: string) => void;
  enumName?: string;
}) => {
  const editor = useSchemaEditor();
  const { isReadonly } = editor;

  const handleToggleChange = () => {
    toggleEnumMode();
  };

  return (
    <div className=" max-w-[500px] rounded-md border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center">
        <Checkbox
          checked={includeEnum}
          onChange={() => {
            toggleEnums();
          }}
          label="Include Enum Values"
          disabled={isReadonly}
        />

        <HelpTooltip content="Define a list of allowed values for this parameter" />
      </div>

      {includeEnum && (
        <div className="mt-3 space-y-4">
          <div className="rounded-md bg-gray-50 p-3">
            <div className="mb-2 flex items-center justify-center">
              <div className="flex items-center gap-3 rounded-full bg-gray-100 p-1 text-xs">
                <span
                  className={`rounded-full px-3 py-1 ${
                    !isEnumVariable ? "bg-white shadow-sm" : ""
                  }`}
                >
                  Default
                </span>
                <Toggle
                  enabled={isEnumVariable}
                  setEnabled={handleToggleChange}
                  disabled={isReadonly}
                />
                <span
                  className={`rounded-full px-3 py-1 ${isEnumVariable ? "bg-white shadow-sm" : ""}`}
                >
                  Use Variable
                </span>
              </div>
            </div>

            {isEnumVariable ? (
              <div className="mt-4 rounded-md bg-white p-3">
                <div className="mb-2 text-sm font-medium text-gray-700">Variable name:</div>
                <div className="flex items-center gap-3">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 z-10 -translate-y-1/2 text-sm text-gray-400">
                      {"{{"}
                    </span>
                    <Input
                      type="text"
                      value={enumName || ""}
                      onChange={e => onNameChange(e.target.value)}
                      placeholder="customEnums"
                      className="pl-8 pr-8 font-mono"
                      readOnly={isReadonly}
                    />
                    <span className="absolute right-3 top-1/2 z-10 -translate-y-1/2 text-sm text-gray-400">
                      {"}}"}
                    </span>
                  </div>
                </div>
                <div className="mt-3 flex items-start gap-2 rounded-md bg-blue-50 p-2 text-xs text-blue-700">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="mt-0.5 h-4 w-4 flex-shrink-0"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    The variable should be an array of strings that will be used as enum values.
                  </div>
                </div>
              </div>
            ) : (
              enums && (
                <div className="mt-4 rounded-md bg-white p-3">
                  <div className="max-h-60 overflow-y-auto p-0.5">
                    {Object.entries(enums).map(([key, value], index) => (
                      <div
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        className="mb-2 flex gap-2 rounded-md hover:bg-gray-50"
                      >
                        <Input
                          type="text"
                          value={value}
                          onChange={e => {
                            setEnum(key, e.target.value);
                          }}
                          placeholder="Enter enum value"
                          className="flex-1"
                          readOnly={isReadonly}
                        />
                        <BasicTooltip content="Remove value">
                          <Button
                            onClick={() => {
                              deleteEnum(key);
                            }}
                            variant="destructiveOutline"
                            disabled={isReadonly}
                            className="h-9 w-9 p-0"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </BasicTooltip>
                      </div>
                    ))}
                  </div>
                  <Button
                    onClick={addEnum}
                    variant="outline"
                    size="sm"
                    disabled={isReadonly}
                    className="mt-2 w-full justify-center"
                  >
                    <PlusIcon className="mr-1 h-3 w-3" /> Add Enum Value
                  </Button>
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default observer(Enums);
